import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RELEASE_NOTES_VERSION, unseenReleaseNotes } from './release-notes';
import { ReleaseNotesDialog } from './release-notes-dialog/release-notes-dialog';
import { AuthService } from '../auth/services/auth.service';

/**
 * Kept across sessions on purpose: the notes are about the app, not the account, so a sign-out
 * must not bring them back. One browser, one showing per version.
 */
export const RELEASE_NOTES_SEEN_KEY = 'RELEASE_NOTES_SEEN_VERSION';

/**
 * Shows what is new once per release. `App` asks after every login and app start; the dialog
 * opens only when this browser has not yet seen the newest entry and the account predates it,
 * and closing it records that it has. Guarded in memory too, so a re-run of the caller's effect cannot open it twice.
 */
@Injectable({
  providedIn: 'root',
})
export class ReleaseNotesService {
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  readonly #shownThisSession = signal(false);

  showIfUnseen(): void {
    if (this.#shownThisSession()) {
      return;
    }
    const notes = unseenReleaseNotes(this.seenVersion(), this.authService.currentUser()?.createdAt);
    this.#shownThisSession.set(true);
    if (notes.length === 0) {
      // Nothing to show, but the version is still done with — the next release is judged
      // against it, not against every entry since the account was created.
      this.markSeen();
      return;
    }

    const dialogRef = this.dialog.open(ReleaseNotesDialog, {
      data: notes,
      width: '560px',
      restoreFocus: false,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(() => this.markSeen());
  }

  private seenVersion(): string | null {
    return localStorage.getItem(RELEASE_NOTES_SEEN_KEY);
  }

  private markSeen(): void {
    localStorage.setItem(RELEASE_NOTES_SEEN_KEY, RELEASE_NOTES_VERSION);
  }
}
